const logger = require('../logger');
const { MAX_RETRIES, RETRY_DELAY } = require('../constants/retry');

/**
 * Execute a task with retry logic and optional network recovery
 * @param {Function} task - Function to execute (must return a Promise)
 * @param {Object} options - Retry options
 * @param {number} [options.retries=10] - Maximum number of retries
 * @param {number} [options.retryDelay=1000] - Base delay in milliseconds for exponential backoff
 * @param {string} [options.taskName='Task'] - Task name for logging
 * @param {boolean} [options.enableNetworkRecovery=false] - Enable network disconnection detection and auto-recovery (browser only)
 * @param {Function} [options.onNetworkDisconnected] - Callback when network disconnection is detected
 * @param {Function} [options.onNetworkReconnected] - Callback when network is reconnected
 * @returns {Promise<any>} - The result of the task
 *
 * @description
 * When enableNetworkRecovery is true and running in browser environment:
 * - Detects network disconnection using navigator.onLine
 * - Pauses retry attempts and waits for network recovery
 * - Automatically retries when network is back online
 * - Resets retry counter after network recovery for a fresh start
 *
 * @example
 * // Basic usage (backward compatible)
 * await executeWithRetry(async () => uploadData(), {
 *     retries: 5,
 *     retryDelay: 1000,
 *     taskName: 'uploadData'
 * });
 *
 * @example
 * // With network recovery enabled
 * await executeWithRetry(async () => uploadData(), {
 *     retries: 5,
 *     retryDelay: 1000,
 *     taskName: 'uploadData',
 *     enableNetworkRecovery: true,
 *     onNetworkDisconnected: () => console.log('Network disconnected'),
 *     onNetworkReconnected: () => console.log('Network reconnected')
 * });
 */
async function executeWithRetry(task, options = {}) {
    const {
        retries = MAX_RETRIES,
        retryDelay = RETRY_DELAY,
        taskName = 'Task',
        enableNetworkRecovery = true,
        onNetworkDisconnected,
        onNetworkReconnected,
    } = options;

    // 检测是否支持网络监听 - 只在浏览器环境且配置启用时生效
    const supportsNetworkMonitor =
        enableNetworkRecovery && typeof window !== 'undefined' && typeof navigator !== 'undefined';

    let onlineListenerAdded = false;
    let networkListener = null;

    // 清理函数 - 移除事件监听器，防止内存泄漏
    const cleanup = () => {
        if (onlineListenerAdded && networkListener) {
            window.removeEventListener('online', networkListener);
            onlineListenerAdded = false;
            networkListener = null;
        }
    };

    // 主重试循环
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const result = await task();
            cleanup(); // 成功时清理
            return result;
        } catch (error) {
            // 检测网络断开
            if (supportsNetworkMonitor && !navigator.onLine && !onlineListenerAdded) {
                logger.log(`[Retry] ${taskName} network disconnected, waiting for reconnection...`);

                // 触发断网回调
                if (onNetworkDisconnected && typeof onNetworkDisconnected === 'function') {
                    try {
                        onNetworkDisconnected();
                    } catch (callbackError) {
                        logger.errorAndLog(`[Retry] ${taskName} onNetworkDisconnected callback error:`, callbackError);
                    }
                }

                // 等待网络恢复
                await new Promise((resolve) => {
                    networkListener = () => {
                        logger.log(`[Retry] ${taskName} network reconnected, resuming task...`);

                        // 触发网络恢复回调
                        if (onNetworkReconnected && typeof onNetworkReconnected === 'function') {
                            try {
                                onNetworkReconnected();
                            } catch (callbackError) {
                                logger.errorAndLog(
                                    `[Retry] ${taskName} onNetworkReconnected callback error:`,
                                    callbackError
                                );
                            }
                        }

                        cleanup();
                        resolve();
                    };

                    window.addEventListener('online', networkListener);
                    onlineListenerAdded = true;
                });

                // 网络恢复后重置重试次数，给任务一个全新的重试机会
                attempt = -1; // 下次循环会变成 0
                continue;
            }

            // 正常的重试逻辑
            if (attempt === retries) {
                cleanup();
                logger.errorAndLog(`[Retry] ${taskName} failed after ${retries} retries. Last error: ${error.message}`);
                throw error;
            }

            const delay = retryDelay * Math.pow(2, attempt); // 指数退避
            logger.log(
                `[Retry] ${taskName} attempt ${attempt + 1}/${retries} failed. Retrying after ${delay}ms. Error: ${
                    error.message
                }`
            );

            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }
}

module.exports = executeWithRetry;
